import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Download, Eye, Plus, Send, X, AlertTriangle } from "lucide-react";

const money = (x) => `$${Number(x || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function ClientInvoices() {
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [cands, setCands] = useState(null);
  const [sel, setSel] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(null); // invoice detail
  const [busy, setBusy] = useState("");
  const [allowOver, setAllowOver] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [sendMsg, setSendMsg] = useState("");

  const loadInvoices = () => api.get("/client-invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/contracts").then((r) => setContracts(r.data.filter((c) => c.status === "confirmed"))).catch((e) => toast.error(apiError(e)));
    loadInvoices();
  }, []);

  useEffect(() => {
    setSel({}); setAllowOver(false);
    if (!contractId) { setCands(null); return; }
    api.get(`/client-invoices/candidates?contract_id=${contractId}`).then((r) => setCands(r.data)).catch((e) => toast.error(apiError(e)));
  }, [contractId]);

  const openInvoice = (id) => {
    api.get(`/client-invoices/${id}`).then((r) => {
      setOpen(r.data);
      api.get(`/client-invoices/${id}/recipients`).then((x) => setRecipients(x.data)).catch(() => {});
    }).catch((e) => toast.error(apiError(e)));
  };

  const selectedTotal = (cands?.candidates || []).filter((c) => sel[c.visit_id]).reduce((n, c) => n + c.amount, 0);
  const po = cands?.po_balance;

  const create = async () => {
    setBusy("create");
    try {
      const { data } = await api.post("/client-invoices", {
        contract_id: contractId, visit_ids: Object.keys(sel).filter((k) => sel[k]), allow_over: allowOver,
      });
      toast.success(`Invoice draft ${data.number} created — the selected dates are reserved.`);
      setSel({}); setAllowOver(false);
      loadInvoices();
      openInvoice(data.id);
    } catch (e) {
      const msg = apiError(e);
      if (msg.includes("OVER_PO")) toast.error("These visits exceed the PO's remaining balance. Tick 'create anyway' only if a PO increase is pending.");
      else toast.error(msg);
    } finally { setBusy(""); }
  };

  const removeVisit = async (visitId) => {
    try {
      const { data } = await api.delete(`/client-invoices/${open.id}/lines/${visitId}`);
      setOpen({ ...open, lines: data.lines });
      toast.success("Visit removed — its date is available again.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this draft? All its dates become available again.")) return;
    try { await api.post(`/client-invoices/${open.id}/cancel`); setOpen(null); loadInvoices(); toast.success("Draft cancelled; dates released."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const finalize = async () => {
    setBusy("final");
    try {
      await api.post(`/client-invoices/${open.id}/finalize`, { allow_over: allowOver });
      toast.success("Invoice finalized — line snapshot retained; dates are now invoiced.");
      openInvoice(open.id); loadInvoices();
    } catch (e) {
      const msg = apiError(e);
      if (msg.includes("OVER_PO")) toast.error("Total exceeds the PO remaining balance. Tick 'finalize anyway' only if a PO increase is confirmed.");
      else toast.error(msg);
    } finally { setBusy(""); }
  };

  const send = async () => {
    setBusy("send");
    try {
      const { data } = await api.post(`/client-invoices/${open.id}/send`, { to, cc, message: sendMsg });
      const failed = (data.results || []).filter((r) => r.status !== "sent");
      if (!failed.length) toast.success(`Invoice emailed to ${data.results.length} recipient(s) — accepted by the provider.`);
      else toast.error(`${data.results.length - failed.length} sent, ${failed.length} failed.`);
      openInvoice(open.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (open) {
    const inv = open;
    const total = inv.lines.reduce((n, l) => n + l.qty * l.rate, 0);
    return (
      <div className="space-y-5 max-w-5xl" data-testid="invoice-editor">
        <div className="flex items-center gap-3">
          <button className="btn-ghost text-xs" onClick={() => { setOpen(null); loadInvoices(); }} data-testid="back-to-invoices">← Invoices</button>
          <h1 className="section-title text-2xl">Invoice {inv.number}</h1>
          <span className={inv.status === "final" ? "badge-approved" : "badge-draft"} data-testid="invoice-status">{inv.status === "final" ? "Final" : "Draft"}</span>
          {inv.emailed && <span className="badge-neutral" data-testid="invoice-emailed">Emailed</span>}
        </div>
        <div className="card p-6 space-y-2 text-sm" data-testid="invoice-meta">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><div className="label !mb-0">Contract</div><div className="font-bold text-navy">{inv.contract_name}{inv.po ? ` · PO ${inv.po}` : ""}</div></div>
            <div><div className="label !mb-0">Issued / due</div><div className="font-bold text-navy">{inv.issue_date} → {inv.due_date} ({inv.terms})</div></div>
            <div><div className="label !mb-0">Bill to</div><div className="font-bold text-navy whitespace-pre-line">{inv.bill_to}{inv.attention ? `\nAttn: ${inv.attention}` : ""}</div></div>
          </div>
          {inv.po_balance?.authorized != null && (
            <div className="text-xs text-[#57534E]" data-testid="invoice-po-balance">
              PO authorized {money(inv.po_balance.authorized)} · finalized {money(inv.po_balance.final)} · in drafts {money(inv.po_balance.draft)} ·
              remaining {money(inv.po_balance.remaining)} · <strong className={inv.po_balance.after < 0 ? "text-[#991B1B]" : ""}>after this invoice {money(inv.po_balance.after)}</strong>
            </div>
          )}
        </div>
        <div className="card overflow-hidden" data-testid="invoice-lines">
          <table className="w-full">
            <thead className="bg-sand/60"><tr>
              <th className="table-th">Date</th><th className="table-th">Service</th><th className="table-th">Consultant</th>
              <th className="table-th text-right">Qty</th><th className="table-th text-right">Rate</th><th className="table-th text-right">Amount</th>
              {inv.status === "draft" && <th className="table-th"></th>}
            </tr></thead>
            <tbody>
              {inv.lines.map((l, i) => (
                <tr key={i} data-testid={`invoice-line-${i}`}>
                  <td className="table-td font-bold">{l.date}</td>
                  <td className="table-td text-sm">{l.description}</td>
                  <td className="table-td text-xs">{l.consultant}</td>
                  <td className="table-td text-right">{l.qty} {l.unit}</td>
                  <td className="table-td text-right">{money(l.rate)}</td>
                  <td className="table-td text-right font-bold">{money(l.qty * l.rate)}</td>
                  {inv.status === "draft" && (
                    <td className="table-td text-right">
                      {(i === 0 || inv.lines[i - 1].visit_id !== l.visit_id) && (
                        <button className="btn-ghost !py-0.5 !px-1.5 text-xs text-[#991B1B]" onClick={() => removeVisit(l.visit_id)} data-testid={`remove-visit-${l.visit_id}`}>
                          <X size={12} /> Remove visit</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              <tr className="bg-sand/40"><td colSpan={5} className="table-td text-right font-black">Total due</td>
                <td className="table-td text-right font-black" data-testid="invoice-total">{money(total)}</td>
                {inv.status === "draft" && <td></td>}</tr>
            </tbody>
          </table>
        </div>
        <div className="card p-6 space-y-3" data-testid="invoice-actions">
          <div className="flex flex-wrap gap-2 items-center">
            <a className="btn-outline !py-1.5 text-xs" href={`${process.env.REACT_APP_BACKEND_URL}/api/client-invoices/${inv.id}/preview`} target="_blank" rel="noreferrer" data-testid="invoice-preview"><Eye size={12} /> Preview</a>
            <a className="btn-outline !py-1.5 text-xs" href={`${process.env.REACT_APP_BACKEND_URL}/api/client-invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" data-testid="invoice-pdf"><Download size={12} /> PDF</a>
            {inv.status === "draft" && (
              <>
                <button className="btn-primary !py-1.5 text-xs" onClick={finalize} disabled={busy === "final"} data-testid="invoice-finalize">{busy === "final" ? "Finalizing…" : "Mark final"}</button>
                <button className="btn-ghost !py-1.5 text-xs text-[#991B1B]" onClick={cancel} data-testid="invoice-cancel">Cancel draft</button>
                {inv.po_balance?.after < 0 && (
                  <label className="flex items-center gap-2 text-xs text-[#991B1B] font-bold" data-testid="allow-over-row">
                    <AlertTriangle size={13} />
                    <input type="checkbox" checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} data-testid="allow-over" />
                    Over PO balance — finalize anyway (PO increase pending)
                  </label>
                )}
              </>
            )}
          </div>
          {inv.status === "final" && (
            <div className="space-y-2 pt-2 border-t border-[#E7E1D4]" data-testid="invoice-send">
              <label className="label">Send invoice to</label>
              <div className="flex flex-wrap gap-2">
                {recipients.map((r) => (
                  <label key={r.email} className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer font-bold ${to.includes(r.email) ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4]"}`} data-testid={`invoice-to-${r.email}`}>
                    <input type="checkbox" className="hidden" checked={to.includes(r.email)}
                      onChange={(e) => setTo(e.target.checked ? [...to, r.email] : to.filter((x) => x !== r.email))} />
                    {r.name || r.email}
                  </label>
                ))}
              </div>
              <input className="input text-sm" placeholder="Short message (optional)" value={sendMsg} data-testid="invoice-message" onChange={(e) => setSendMsg(e.target.value)} />
              <button className="btn-primary" onClick={send} disabled={busy === "send" || !to.length} data-testid="invoice-send-button">
                <Send size={14} /> {busy === "send" ? "Sending…" : "Send invoice"}</button>
              {inv.emailed && <div className="text-xs text-[#78716C]" data-testid="invoice-sent-log">
                Sent {inv.emailed.at?.slice(0, 10)} by {inv.emailed.by} — {(inv.emailed.results || []).map((r) => `${r.email}: ${r.status}`).join(", ")} (provider-accepted ≠ confirmed delivery)</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="client-invoices-page">
      <div>
        <p className="eyebrow">Admin</p>
        <h1 className="section-title text-3xl mt-1">Client Invoices</h1>
        <p className="text-sm text-[#57534E] mt-1 max-w-2xl">Select approved visit dates to include — nothing is invoiced automatically. Creating a draft reserves those services in one protected step, so the same date can never be billed twice.</p>
      </div>

      <div className="card p-6 space-y-4" data-testid="invoice-builder">
        <div className="max-w-md">
          <label className="label">Contract</label>
          <select className="input" value={contractId} data-testid="builder-contract" onChange={(e) => setContractId(e.target.value)}>
            <option value="">Choose a confirmed contract…</option>
            {contracts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.po ? ` · PO ${c.po}` : ""}</option>)}
          </select>
        </div>
        {po && po.authorized != null && (
          <div className="text-xs text-[#57534E]" data-testid="builder-po-bar">
            Authorized {money(po.authorized)} · finalized {money(po.final)} · in drafts {money(po.draft)} · remaining {money(po.remaining)}
            {selectedTotal > 0 && <> · <strong className={po.remaining - selectedTotal < 0 ? "text-[#991B1B]" : ""}>after selection {money(po.remaining - selectedTotal)}</strong></>}
          </div>
        )}
        {cands && cands.stale_count > 0 && (
          <p className="text-xs text-[#92400E]" data-testid="stale-note">{cands.stale_count} approved log(s) changed or were withdrawn since approval — they reappear here only after re-approval.</p>
        )}
        {cands && (
          <div className="overflow-x-auto" data-testid="candidates-table">
            <table className="w-full">
              <thead className="bg-sand/60"><tr>
                <th className="table-th"></th><th className="table-th">Date</th><th className="table-th">Consultant</th>
                <th className="table-th">School</th><th className="table-th">Services</th><th className="table-th text-right">Amount</th>
              </tr></thead>
              <tbody>
                {cands.candidates.map((c) => {
                  const blocked = c.problems.length > 0;
                  return (
                    <tr key={c.visit_id} className={blocked ? "opacity-60" : c.duplicate_day ? "bg-[#FEF2F2]" : ""} data-testid={`candidate-${c.visit_id}`}>
                      <td className="table-td">
                        <input type="checkbox" disabled={blocked} checked={!!sel[c.visit_id]} data-testid={`select-${c.visit_id}`}
                          onChange={(e) => setSel({ ...sel, [c.visit_id]: e.target.checked })} />
                      </td>
                      <td className="table-td font-bold">{c.date}</td>
                      <td className="table-td">{c.consultant_name}</td>
                      <td className="table-td">{c.school_name}</td>
                      <td className="table-td text-xs">{c.services}
                        {blocked && <div className="text-[#991B1B] font-bold" data-testid={`blocked-${c.visit_id}`}>{c.problems.join(" · ")}</div>}
                        {c.duplicate_day && <div className="text-[#991B1B] font-bold">Same consultant + date appears twice — keep only one</div>}
                      </td>
                      <td className="table-td text-right font-bold">{blocked ? "—" : money(c.amount)}</td>
                    </tr>
                  );
                })}
                {cands.candidates.length === 0 && <tr><td colSpan={6} className="table-td text-center text-[#78716C] py-8" data-testid="candidates-empty">No approved, uninvoiced visits for this contract.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {cands && Object.values(sel).some(Boolean) && (
          <div className="flex flex-wrap items-center gap-3" data-testid="builder-actions">
            <span className="text-sm"><strong>{Object.values(sel).filter(Boolean).length}</strong> selected · <strong data-testid="selected-total">{money(selectedTotal)}</strong></span>
            {po && po.authorized != null && po.remaining - selectedTotal < 0 && (
              <label className="flex items-center gap-2 text-xs text-[#991B1B] font-bold" data-testid="builder-over-row">
                <AlertTriangle size={13} />
                <input type="checkbox" checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} data-testid="builder-allow-over" />
                Over PO balance — create anyway (PO increase pending)
              </label>
            )}
            <button className="btn-primary" onClick={create} disabled={busy === "create"} data-testid="generate-invoice">
              <Plus size={15} /> {busy === "create" ? "Reserving…" : "Generate invoice draft"}</button>
          </div>
        )}
      </div>

      <div className="card overflow-hidden" data-testid="invoices-table">
        <table className="w-full">
          <thead className="bg-sand/60"><tr>
            <th className="table-th">Number</th><th className="table-th">Contract</th><th className="table-th">Issued</th>
            <th className="table-th">Lines</th><th className="table-th text-right">Total</th><th className="table-th">Status</th>
          </tr></thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="hover:bg-canvas transition-colors cursor-pointer" onClick={() => openInvoice(i.id)} data-testid={`invoice-row-${i.id}`}>
                <td className="table-td font-bold">{i.number}</td>
                <td className="table-td">{i.contract_name}</td>
                <td className="table-td text-xs">{i.issue_date}</td>
                <td className="table-td">{i.lines.length}</td>
                <td className="table-td text-right font-bold">{money(i.total)}</td>
                <td className="table-td"><span className={i.status === "final" ? "badge-approved" : "badge-draft"}>{i.status}</span>
                  {i.emailed && <span className="badge-neutral ml-1">emailed</span>}</td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={6} className="table-td text-center text-[#78716C] py-10" data-testid="invoices-empty">No client invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
